import { COOKIE_EXPIRATION_DAYS, COOKIE_KEY, eraseCookie, getCookie, setCookie } from './cookiesUtils';

const AUTH_TOKEN_OBFUSCATION_SECRET = 'ims-floorplanner-auth-token$7fQ2pL9z';

interface StoredAuthToken {
  authToken: string;
  shouldRewrite: boolean;
}

// Client-side obfuscation only. This hides the raw token from casual cookie inspection, but does not replace an HttpOnly server cookie.
const toBase64Url = (value: string): string => window.btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '');

const fromBase64Url = (value: string): string => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const paddedBase64 = base64.padEnd(base64.length + (4 - (base64.length % 4 || 4)), '=');

  return window.atob(paddedBase64);
};

const xorBytes = (bytes: Uint8Array): Uint8Array => {
  const secretBytes = new TextEncoder().encode(AUTH_TOKEN_OBFUSCATION_SECRET);

  return bytes.map((byte, index) => byte ^ secretBytes[index % secretBytes.length]);
};

const obfuscateAuthToken = (token: string): string => {
  const tokenBytes = new TextEncoder().encode(token);
  const obfuscatedBytes = xorBytes(tokenBytes);
  const binaryValue = Array.from(obfuscatedBytes, (byte) => String.fromCharCode(byte)).join('');

  return toBase64Url(binaryValue);
};

const deobfuscateAuthToken = (value: string): string | null => {
  try {
    const binaryValue = fromBase64Url(value);
    const obfuscatedBytes = Uint8Array.from(binaryValue, (char) => char.charCodeAt(0));
    const tokenBytes = xorBytes(obfuscatedBytes);

    return new TextDecoder().decode(tokenBytes);
  } catch {
    return null;
  }
};

const isInvalidAuthTokenChar = (char: string): boolean => {
  const charCode = char.charCodeAt(0);

  return charCode <= 31 || charCode === 127 || char === ';' || /\s/u.test(char);
};

const isPlausibleAuthToken = (value: string): boolean => value.length >= 10 && !value.includes('\uFFFD') && !Array.from(value).some(isInvalidAuthTokenChar);

const parseStoredAuthToken = (storedToken: string): StoredAuthToken | null => {
  const authToken = deobfuscateAuthToken(storedToken);

  if (authToken && isPlausibleAuthToken(authToken)) {
    return { authToken, shouldRewrite: false };
  }

  return { authToken: storedToken, shouldRewrite: true };
};

export const readStoredAuthToken = (): string | null => {
  const storedToken = getCookie(COOKIE_KEY);

  if (!storedToken) {
    return null;
  }

  const parsedToken = parseStoredAuthToken(storedToken);

  if (!parsedToken) {
    eraseCookie(COOKIE_KEY);
    return null;
  }

  if (parsedToken.shouldRewrite) {
    setCookie(COOKIE_KEY, obfuscateAuthToken(parsedToken.authToken), COOKIE_EXPIRATION_DAYS);
  }

  return parsedToken.authToken;
};

export const storeAuthToken = (authToken: string): void => {
  setCookie(COOKIE_KEY, obfuscateAuthToken(authToken), COOKIE_EXPIRATION_DAYS);
};

export const clearStoredAuthToken = (): void => {
  eraseCookie(COOKIE_KEY);
};
